/**
 * @fileoverview Pre-Appointment pipeline table view (MOR-882)
 *
 * Grouped, sortable table over pipeline jobs — mirrors a slimmed-down
 * `src/calendar/components/list/task-list-view.tsx` (grouped sections +
 * click-to-sort headers over shadcn Table primitives).
 */

import { useMemo } from "react";
import { format } from "date-fns";
import { ArrowDown, ArrowUp, Handshake, ShieldAlert } from "lucide-react";

import type { Id } from "@cvx/_generated/dataModel";
import type {
  PreAppointmentViewGroupBy,
  PreAppointmentViewSortBy,
} from "@cvx/preAppointment/preAppointmentViews/schema";

import {
  APPOINTMENT_TYPE_LABELS,
  NO_REFERRER_KEY,
  NO_TYPE_KEY,
  UNSTAGED_KEY,
  type PipelineJob,
  type PipelineLabel,
  type PipelineStatus,
} from "./constants";
import { JOB_STATUS_LABELS, JOB_STATUS_VARIANTS } from "@/lib/job-statuses";
import { Badge, type BadgeColor } from "@/ui/primitives/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/composed/data-display/table";
import { cn } from "@/utils/misc";
import { CustomFieldBadge } from "./custom-field-badge";
import {
  type CustomFieldDef,
  customFieldTotals,
  formatCustomFieldValue,
  isSummable,
} from "./custom-fields";

export type PipelineSortDir = "asc" | "desc";

interface Group {
  key: string;
  title: string;
  color?: BadgeColor;
  jobs: PipelineJob[];
}

function compareBy(
  sortBy: PreAppointmentViewSortBy,
  statusOrder: Map<string, number>,
) {
  return (a: PipelineJob, b: PipelineJob): number => {
    switch (sortBy) {
      case "name":
        return a.entityName.localeCompare(b.entityName);
      case "status": {
        const ao = a.preAppointmentStatusId
          ? (statusOrder.get(String(a.preAppointmentStatusId)) ?? -1)
          : -1;
        const bo = b.preAppointmentStatusId
          ? (statusOrder.get(String(b.preAppointmentStatusId)) ?? -1)
          : -1;
        return ao - bo;
      }
      case "manual": {
        const ar = a.preAppointmentBoardRank ?? "￿";
        const br = b.preAppointmentBoardRank ?? "￿";
        return ar < br ? -1 : ar > br ? 1 : 0;
      }
      case "created":
      default:
        return b._creationTime - a._creationTime;
    }
  };
}

export function PipelineList({
  jobs,
  statuses,
  labels,
  customFields,
  groupBy,
  sortBy,
  sortDir,
  onSortChange,
  onRowClick,
  onOpenReferrer,
}: {
  jobs: PipelineJob[];
  statuses: PipelineStatus[];
  labels: PipelineLabel[];
  customFields: CustomFieldDef[];
  groupBy: PreAppointmentViewGroupBy;
  sortBy: PreAppointmentViewSortBy;
  sortDir: PipelineSortDir;
  onSortChange: (
    sortBy: PreAppointmentViewSortBy,
    dir: PipelineSortDir,
  ) => void;
  onRowClick: (job: PipelineJob) => void;
  /** Open the referrer's firm-contact drawer (mail's contact-panel pattern). */
  onOpenReferrer: (firmContactId: Id<"firmContacts">) => void;
}) {
  const statusById = useMemo(
    () => new Map(statuses.map((s) => [String(s._id), s])),
    [statuses],
  );
  const statusOrder = useMemo(
    () => new Map(statuses.map((s) => [String(s._id), s.sortOrder])),
    [statuses],
  );
  const labelById = useMemo(
    () => new Map(labels.map((l) => [String(l._id), l])),
    [labels],
  );

  const grandTotals = useMemo(
    () =>
      new Map(
        customFieldTotals(customFields, jobs).map(({ field, total }) => [
          field._id,
          total,
        ]),
      ),
    [customFields, jobs],
  );

  const groups = useMemo<Group[]>(() => {
    const cmp = compareBy(sortBy, statusOrder);
    const sortJobs = (list: PipelineJob[]) => {
      const sorted = [...list].sort(cmp);
      return sortDir === "desc" ? sorted.reverse() : sorted;
    };

    if (groupBy === "none") {
      return [{ key: "all", title: "All jobs", jobs: sortJobs(jobs) }];
    }

    const buckets = new Map<string, PipelineJob[]>();
    const push = (key: string, job: PipelineJob) => {
      const list = buckets.get(key) ?? [];
      list.push(job);
      buckets.set(key, list);
    };

    if (groupBy === "referrer") {
      for (const job of jobs) {
        push(job.referrer ? String(job.referrer._id) : NO_REFERRER_KEY, job);
      }
      const out: Group[] = [...buckets.entries()]
        .filter(([key]) => key !== NO_REFERRER_KEY)
        .map(([key, list]) => ({
          key,
          title: list[0]?.referrer?.displayName ?? "Referrer",
          jobs: sortJobs(list),
        }))
        .sort((a, b) => a.title.localeCompare(b.title));
      const rest = buckets.get(NO_REFERRER_KEY);
      if (rest?.length) {
        out.push({
          key: NO_REFERRER_KEY,
          title: "No referrer",
          jobs: sortJobs(rest),
        });
      }
      return out;
    }

    if (groupBy === "appointmentType") {
      for (const job of jobs) {
        push(job.appointmentType ?? NO_TYPE_KEY, job);
      }
      const out: Group[] = [...buckets.entries()]
        .filter(([key]) => key !== NO_TYPE_KEY)
        .map(([key, list]) => ({
          key,
          title:
            APPOINTMENT_TYPE_LABELS[
              key as keyof typeof APPOINTMENT_TYPE_LABELS
            ] ?? key,
          jobs: sortJobs(list),
        }))
        .sort((a, b) => a.title.localeCompare(b.title));
      const rest = buckets.get(NO_TYPE_KEY);
      if (rest?.length) {
        out.push({
          key: NO_TYPE_KEY,
          title: "No type yet",
          jobs: sortJobs(rest),
        });
      }
      return out;
    }

    // Group by pipeline stage, in stage order.
    for (const job of jobs) {
      push(
        job.preAppointmentStatusId
          ? String(job.preAppointmentStatusId)
          : UNSTAGED_KEY,
        job,
      );
    }
    const out: Group[] = [];
    const unstaged = buckets.get(UNSTAGED_KEY);
    if (unstaged?.length) {
      out.push({
        key: UNSTAGED_KEY,
        title: "Unstaged",
        jobs: sortJobs(unstaged),
      });
    }
    for (const s of statuses) {
      const list = buckets.get(String(s._id));
      if (!list?.length) continue;
      out.push({
        key: String(s._id),
        title: s.name,
        color: s.color as BadgeColor,
        jobs: sortJobs(list),
      });
    }
    return out;
  }, [jobs, groupBy, sortBy, sortDir, statuses, statusOrder]);

  const sortHeader = (
    label: string,
    key: PreAppointmentViewSortBy,
    className?: string,
  ) => (
    <TableHead
      className={cn("cursor-pointer select-none", className)}
      onClick={() =>
        onSortChange(key, sortBy === key && sortDir === "asc" ? "desc" : "asc")
      }
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sortBy === key &&
          (sortDir === "asc" ? (
            <ArrowUp className="size-3" />
          ) : (
            <ArrowDown className="size-3" />
          ))}
      </span>
    </TableHead>
  );

  return (
    <div className="overflow-hidden rounded-lg border bg-background">
      <Table>
        <TableHeader>
          <TableRow>
            {sortHeader("Job", "name", "w-[28%]")}
            {sortHeader("Stage", "status")}
            <TableHead>Labels</TableHead>
            {customFields.map((field) => (
              <TableHead key={field._id}>{field.name}</TableHead>
            ))}
            <TableHead>Referrer</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Outcome</TableHead>
            {sortHeader("Created", "created")}
          </TableRow>
        </TableHeader>
        <TableBody>
          {groups.map((group) => (
            <GroupRows
              key={group.key}
              group={group}
              showHeader={groupBy !== "none"}
              statusById={statusById}
              labelById={labelById}
              customFields={customFields}
              onRowClick={onRowClick}
              onOpenReferrer={onOpenReferrer}
            />
          ))}
          {groups.every((g) => g.jobs.length === 0) && (
            <TableRow>
              <TableCell
                colSpan={7 + customFields.length}
                className="h-24 text-center text-sm text-muted-foreground"
              >
                No pre-appointment jobs match the current filters.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
        {/*
          Grand totals aligned under their own columns (MOR-948). Only shown
          when at least one visible field can be summed — a table of text
          fields has nothing to add up.
        */}
        {jobs.length > 0 && customFields.some((f) => isSummable(f.type)) && (
          <TableFooter>
            <TableRow className="hover:bg-transparent">
              <TableCell className="text-xs font-medium">Total</TableCell>
              <TableCell />
              <TableCell />
              {customFields.map((field) => {
                const total = grandTotals.get(field._id);
                return (
                  <TableCell
                    key={field._id}
                    className="text-xs font-medium tabular-nums"
                  >
                    {total === undefined
                      ? ""
                      : formatCustomFieldValue(field.type, String(total))}
                  </TableCell>
                );
              })}
              <TableCell />
              <TableCell />
              <TableCell />
              <TableCell />
            </TableRow>
          </TableFooter>
        )}
      </Table>
    </div>
  );
}

function GroupRows({
  group,
  showHeader,
  statusById,
  labelById,
  customFields,
  onRowClick,
  onOpenReferrer,
}: {
  group: Group;
  showHeader: boolean;
  statusById: Map<string, PipelineStatus>;
  labelById: Map<string, PipelineLabel>;
  customFields: CustomFieldDef[];
  onRowClick: (job: PipelineJob) => void;
  /** Open the referrer's firm-contact drawer (mail's contact-panel pattern). */
  onOpenReferrer: (firmContactId: Id<"firmContacts">) => void;
}) {
  return (
    <>
      {showHeader && (
        <TableRow className="bg-muted/50 hover:bg-muted/50">
          <TableCell colSpan={7 + customFields.length} className="py-1.5">
            <span className="flex items-center gap-2">
              {group.color ? (
                <Badge variant="soft" color={group.color} size="sm">
                  {group.title}
                </Badge>
              ) : (
                <span className="text-xs font-medium">{group.title}</span>
              )}
              <span className="text-xs tabular-nums text-muted-foreground">
                {group.jobs.length}
              </span>
              {/* What this group is worth, for summable fields (MOR-948). */}
              {customFieldTotals(customFields, group.jobs).map(
                ({ field, total }) => (
                  <span
                    key={field._id}
                    className="text-xs text-muted-foreground"
                  >
                    {field.name}{" "}
                    <span className="font-medium tabular-nums text-foreground">
                      {formatCustomFieldValue(field.type, String(total))}
                    </span>
                  </span>
                ),
              )}
            </span>
          </TableCell>
        </TableRow>
      )}
      {group.jobs.map((job) => {
        const stage = job.preAppointmentStatusId
          ? statusById.get(String(job.preAppointmentStatusId))
          : undefined;
        const jobLabels = job.preAppointmentLabelIds
          .map((id) => labelById.get(String(id)))
          .filter((l): l is PipelineLabel => Boolean(l));
        return (
          <TableRow
            key={String(job._id)}
            className="cursor-pointer"
            onClick={() => onRowClick(job)}
          >
            <TableCell>
              <span className="flex items-center gap-1.5">
                <span className="truncate text-sm font-medium">
                  {job.entityName}
                </span>
                {job.openConflictCheckCount > 0 && (
                  <ShieldAlert
                    className="size-3.5 shrink-0 text-amber-600"
                    aria-label="Open conflict check"
                  />
                )}
              </span>
              {job.jobName && (
                <span className="block truncate text-xs text-muted-foreground">
                  {job.jobName}
                </span>
              )}
            </TableCell>
            <TableCell>
              {stage ? (
                <Badge
                  variant="soft"
                  size="sm"
                  color={stage.color as BadgeColor}
                >
                  {stage.name}
                </Badge>
              ) : (
                <span className="text-xs text-muted-foreground">—</span>
              )}
            </TableCell>
            <TableCell>
              <span className="flex flex-wrap gap-1">
                {jobLabels.map((label) => (
                  <Badge
                    key={String(label._id)}
                    variant="soft"
                    size="sm"
                    color={label.color as BadgeColor}
                  >
                    {label.name}
                  </Badge>
                ))}
              </span>
            </TableCell>
            {customFields.map((field) => {
              const value =
                job.preAppointmentCustomFields[
                  field._id as keyof typeof job.preAppointmentCustomFields
                ];
              return (
                <TableCell key={field._id}>
                  {value ? (
                    <CustomFieldBadge field={field} value={value} />
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </TableCell>
              );
            })}
            <TableCell onClick={(e) => e.stopPropagation()}>
              {job.referrer ? (
                <button
                  type="button"
                  className="flex items-center gap-1.5 text-sm underline-offset-2 hover:underline"
                  onClick={() => {
                    if (job.referrer) onOpenReferrer(job.referrer._id);
                  }}
                >
                  <Handshake className="size-3.5 text-muted-foreground" />
                  <span className="truncate">
                    {job.referrer.displayName}
                    {job.referrer.parentDisplayName
                      ? ` (${job.referrer.parentDisplayName})`
                      : ""}
                  </span>
                </button>
              ) : (
                <span className="text-xs text-muted-foreground">—</span>
              )}
            </TableCell>
            <TableCell>
              {job.appointmentType ? (
                <Badge variant="outline" size="sm">
                  {APPOINTMENT_TYPE_LABELS[job.appointmentType]}
                </Badge>
              ) : (
                <span className="text-xs text-muted-foreground">—</span>
              )}
            </TableCell>
            <TableCell>
              <Badge variant={JOB_STATUS_VARIANTS[job.status]} size="sm">
                {JOB_STATUS_LABELS[job.status]}
              </Badge>
            </TableCell>
            <TableCell className="text-xs text-muted-foreground">
              {format(job._creationTime, "d MMM yyyy")}
            </TableCell>
          </TableRow>
        );
      })}
    </>
  );
}
